import { useEffect, useRef } from "react";
import { useUiStore } from "@/stores/uiStore";
import type { ToastPayload } from "@/stores/uiStore";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

const ICONS = {
  success: { Icon: CheckCircle2, color: "text-success-ink", bg: "bg-success/10 border-success/30" },
  error: { Icon: AlertCircle, color: "text-danger-ink", bg: "bg-danger/10 border-danger/30" },
  info: { Icon: Info, color: "text-info-ink", bg: "bg-info/10 border-info/30" },
};

/** Le temps de lire un titre et une ligne. */
const DUREE_PAR_DEFAUT_MS = 4500;

/**
 * Les notifications.
 *
 * Le conteneur est toujours présent, même vide : un lecteur d'écran n'annonce
 * une zone `aria-live` que si elle existait avant qu'on y écrive.
 */
export function ToastViewport() {
  const toasts = useUiStore((s) => s.toasts);

  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 left-4 sm:left-auto z-[100] flex flex-col items-stretch sm:items-end gap-2 pointer-events-none"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} id={t.id} />
      ))}
    </div>
  );
}

function ToastItem({ toast: t, id }: { toast: ToastPayload; id: string }) {
  const dismiss = useUiStore((s) => s.dismissToast);
  const { Icon, color, bg } = ICONS[t.type];

  // Une erreur reste jusqu'à ce qu'on la ferme. Les messages du lot 3 disent
  // « la facture F-… est créée mais n'est pas partie » : c'est précisément
  // ce qu'on ne doit pas manquer, et 4,5 secondes ne suffisent pas à le lire.
  const persistante = t.type === "error";
  const duree = t.duration ?? DUREE_PAR_DEFAUT_MS;

  // Le compte à rebours s'arrête tant que le pointeur ou le focus est dessus,
  // et reprend là où il en était — pas depuis le début.
  const restant = useRef(duree);
  const debut = useRef(0);
  const minuterie = useRef<number | undefined>(undefined);

  const suspendre = () => {
    if (minuterie.current === undefined) return;
    window.clearTimeout(minuterie.current);
    minuterie.current = undefined;
    restant.current -= Date.now() - debut.current;
  };
  const reprendre = () => {
    if (persistante || minuterie.current !== undefined) return;
    debut.current = Date.now();
    minuterie.current = window.setTimeout(
      () => dismiss(id, "timeout"),
      Math.max(restant.current, 0),
    );
  };

  useEffect(() => {
    reprendre();
    // `suspendre` et non un simple clearTimeout : le minuteur doit repartir
    // si l'effet est rejoué (StrictMode), et il ne repart que s'il est vide.
    return suspendre;
    // Une seule fois par notification : le minuteur porte son propre état.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role={persistante ? "alert" : "status"}
      onPointerEnter={suspendre}
      onPointerLeave={reprendre}
      onFocus={suspendre}
      onBlur={reprendre}
      className={cn(
        "pointer-events-auto w-full sm:max-w-sm flex items-start gap-3 p-3 rounded-xl border bg-bg-card shadow-lg",
        bg,
      )}
    >
      <Icon size={18} className={cn("flex-shrink-0 mt-0.5", color)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary">{t.title}</p>
        {t.message && <p className="text-xs text-text-muted mt-0.5">{t.message}</p>}
      </div>
      {t.action && (
        <button
          type="button"
          onClick={() => dismiss(id, "action")}
          className="shrink-0 -my-1 px-3 py-1.5 text-sm font-semibold text-accent-ink hover:bg-bg-card-hover active:bg-bg-card-hover"
        >
          {t.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => dismiss(id, "dismiss")}
        className="shrink-0 -m-1.5 p-1.5 text-text-muted hover:text-text-primary"
        aria-label="Fermer"
      >
        <X size={14} />
      </button>
    </div>
  );
}
